// Service receipts (integration contract §10): helpers shared by the services list and the service form.
import { AnyAction } from 'redux';
import { ThunkDispatch } from 'redux-thunk';
import { AppState } from '../../../redux/reducers';
import * as AzureFileUploadStore from '../../../redux/middleware/AzureFileUpload.Middleware';
import { Service } from '../../../models/Entities/Service';
import { AzureProtocolSponsorEntry } from '../../../redux/states/AzureFilesState';
import {
  ServiceReceiptTargetResult,
  buildServiceReceiptTarget,
} from '../../../functions/AzureFileUpload.Functions';

/** Sponsor resolved per protocol id (owner of the receipts folder) */
export type ProtocolSponsors = Record<number, AzureProtocolSponsorEntry>;

export interface ServiceReceiptsConnectedProps {
  protocolSponsors: ProtocolSponsors;
}

export interface ServiceReceiptsDispatchProps {
  /** Resolves (and caches) the sponsor of a protocol through the middleware */
  ResolveProtocolSponsor: (protocolId: number) => Promise<number | undefined>;
}

const EMPTY_SPONSORS: ProtocolSponsors = {};

export const mapReceiptsStateToProps = (state: AppState): ServiceReceiptsConnectedProps => ({
  protocolSponsors: state.AzureFiles?.protocolSponsors ?? EMPTY_SPONSORS,
});

export const mapReceiptsDispatchToProps = (
  dispatch: ThunkDispatch<AppState, unknown, AnyAction>
): ServiceReceiptsDispatchProps => ({
  ResolveProtocolSponsor: (protocolId) =>
    dispatch(AzureFileUploadStore.ActionCreators.ResolveProtocolSponsor(protocolId)),
});

/** Upload target of the receipts of a service, or the reason why they cannot be managed yet. */
export const getServiceReceiptTarget = (
  service: Pick<Service, 'id' | 'protocolId'> | null | undefined,
  protocolSponsors: ProtocolSponsors
): ServiceReceiptTargetResult =>
  buildServiceReceiptTarget(service, service?.protocolId ? protocolSponsors[service.protocolId] : undefined);

/** Protocols of the given services whose sponsor was never requested (deduplicated). */
export const getProtocolIdsToResolve = (
  services: Pick<Service, 'protocolId'>[],
  protocolSponsors: ProtocolSponsors
): number[] =>
  Array.from(
    new Set(
      services
        .map((service) => service.protocolId)
        .filter((id): id is number => typeof id === 'number' && Number.isInteger(id) && id > 0)
    )
  ).filter((id) => protocolSponsors[id] === undefined);

/** Receipts shown in the list / form: the count sent by MedicalProtocols (0 when missing). */
export const getServiceReceiptCount = (service: Pick<Service, 'receiptCount'> | null | undefined): number =>
  typeof service?.receiptCount === 'number' && service.receiptCount > 0 ? service.receiptCount : 0;

/** "Adjunta comprobante" is checked but the service has no receipt yet. */
export const isReceiptMissing = (attachReceipt: boolean | undefined, receiptCount: number): boolean =>
  !!attachReceipt && receiptCount === 0;

/** Attribute that marks each grid row with its service id (delegated file drop). */
export const SERVICE_ROW_ATTRIBUTE = 'data-service-id';

/** Service id of the row under the pointer (the drop target), or undefined when it is not over a row. */
export const getDropServiceId = (target: EventTarget | null | undefined): string | undefined => {
  const element = target as Element | null | undefined;
  if (!element || typeof element.closest !== 'function') return undefined;
  const row = element.closest(`[${SERVICE_ROW_ATTRIBUTE}]`);
  return row?.getAttribute(SERVICE_ROW_ATTRIBUTE) || undefined;
};
